import type { Scenario } from "@/engine/types";
import { validateScenario } from "@/engine/scenario.schema";
import { act1Events } from "./events/act1-deadline-request";
import { act2Events } from "./events/act2-branch-reactions";
import { act3Events } from "./events/act3-midproject-risks";
import { act4Events } from "./events/act4-go-live";

const impossibleDeadline: Scenario = {
  id: "impossible-deadline",
  title: "The Impossible Four-Week Deadline",
  premise: [
    "You're the newly hired Data Systems Analyst at a large academic health system.",
    "Leadership wants the first hospital's encounter dashboard migrated to AWS. Nine more hospitals are waiting behind this one.",
    "Every decision below moves five project metrics. There's no single correct path — only tradeoffs and what they cost later.",
  ],
  initialMetrics: {
    schedule: 100,
    budget: 100,
    quality: 100,
    teamTrust: 100,
    stakeholderTrust: 100,
    technicalDebt: 0,
    operationalRisk: 0,
  },
  events: [...act1Events, ...act2Events, ...act3Events, ...act4Events],
  endWhen: (state) =>
    state.resolvedEventIds.includes("hospital_one_retro") ||
    state.day > 20 ||
    state.schedule <= 0 ||
    state.budget <= 0,
  outcomes: [
    {
      id: "critical_failure",
      label: "Project Pulled",
      when: (state) => state.schedule <= 0 || state.budget <= 0,
      summary:
        "Schedule or budget bottomed out before go-live. Leadership pulls the project for a replan — hospital #2 doesn't start until someone rebuilds confidence in the estimate.",
    },
    {
      id: "strong_delivery",
      label: "Strong Delivery",
      when: (state) =>
        state.schedule >= 60 && state.quality >= 60 && state.stakeholderTrust >= 60 && state.teamTrust >= 60,
      summary:
        "Hospital #1 went live on a credible timeline, the team trusts the process, and leadership trusts your numbers. Hospital #2 starts from a real baseline, not a guess.",
    },
    {
      id: "salvaged_but_costly",
      label: "Salvaged, at a Cost",
      when: (state) => state.quality >= 40 && state.stakeholderTrust >= 30,
      summary:
        "The dashboard shipped and mostly works, but it cost trust, debt, or budget along the way. Hospital #2 is possible, but you're starting it with less goodwill than you'd like.",
    },
    {
      id: "pyrrhic_victory",
      label: "Technically Live",
      when: () => true,
      summary:
        "The dashboard is live, but quality, trust, or risk took real damage getting there. The next nine hospitals will be harder than they needed to be.",
    },
  ],
};

validateScenario(impossibleDeadline);

export { impossibleDeadline };
